import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  getSchools,
  deleteSchool,
  editSchool,
} from "../../../services/operations/schoolAPI";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SchoolManagement = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  // EDIT MODAL
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    schoolId: "",
    name: "",
    email: "",
    address: "",
  });

  // DELETE MODAL
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const navigate = useNavigate();

  // LOAD SCHOOLS
  useEffect(() => {
    const load = async () => {
      const data = await getSchools();
      setSchools(data || []);
      setLoading(false);
    };
    load();
  }, []);

  const openEdit = (school) => {
    setEditForm({
      schoolId: school._id,
      name: school.name,
      email: school.email,
      address: school.address,
    });
    setIsEditOpen(true);
  };

  const openDelete = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const success = await editSchool(editForm);
    if (success) {
      setSchools((prev) =>
        prev.map((sch) =>
          sch._id === editForm.schoolId
            ? {
                ...sch,
                name: editForm.name,
                email: editForm.email,
                address: editForm.address,
                updatedAt: new Date().toISOString(),
              }
            : sch
        )
      );

      setIsEditOpen(false);
    }
  };

  const confirmDelete = async () => {
    const success = await deleteSchool(deleteId);
    if (success) {
      setSchools((prev) => prev.filter((sch) => sch._id !== deleteId));
    }
    setIsDeleteOpen(false);
  };

  if (loading)
    return <p className="text-center mt-10 text-gray-500">Loading...</p>;

  return (
    <div className="p-6 bg-white min-h-screen">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">School Management</h1>
        <button
          onClick={() => navigate("/dashboard/super-admin/schools/add-school")}
          className="px-5 py-2 bg-[#012950] hover:bg-blue-900 text-white rounded-lg shadow-md"
        >
          + Add School
        </button>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 overflow-x-auto">
        <table className="w-full text-left text-gray-700 min-w-[900px]">
          <thead>
            <tr className="border-b text-gray-500 text-sm">
              <th className="py-3">School</th>
              <th className="py-3">Email</th>
              <th className="py-3">Address</th>
              <th className="py-3">Subscription</th>
              <th className="py-3">Created At</th>
              <th className="py-3">Updated At</th>
              <th className="py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {schools.map((school, i) => (
              <motion.tr
                key={school._id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="border-b hover:bg-gray-50 cursor-pointer"
                onClick={() =>
                  navigate(
                    `/dashboard/super-admin/schools/${school._id}`
                  )
                }
              >
                <td className="py-3">{school.name}</td>
                <td className="py-3">{school.email}</td>
                <td className="py-3 text-gray-600">{school.address}</td>

                <td className="py-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      school.subscriptionStatus === "active"
                        ? "bg-green-100 text-green-700"
                        : school.subscriptionStatus === "expired"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {school.subscriptionStatus}
                  </span>
                </td>

                <td className="py-3 text-sm text-gray-600">
                  {formatDate(school.createdAt)}
                </td>

                <td className="py-3 text-sm text-gray-600">
                  {formatDate(school.updatedAt)}
                </td>

<td className="py-3 flex justify-end space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Stops the row click from firing
                      openEdit(school);
                    }}
                    className="p-2 bg-blue-100 rounded-lg hover:bg-blue-200"
                  >
                    <PencilIcon className="h-5 w-5 text-blue-700" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Stops the row click from firing
                      openDelete(school._id);
                    }}
                    className="p-2 bg-red-100 rounded-lg hover:bg-red-200"
                  >
                    <TrashIcon className="h-5 w-5 text-red-700" />
                  </button>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* EDIT MODAL */}
      {isEditOpen && (
        <EditModal
          form={editForm}
          setForm={setEditForm}
          onSubmit={handleEditSubmit}
          onClose={() => setIsEditOpen(false)}
        />
      )}

      {/* DELETE MODAL */}
      {isDeleteOpen && (
        <DeleteModal
          onConfirm={confirmDelete}
          onClose={() => setIsDeleteOpen(false)}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------
// EDIT MODAL
// -----------------------------------------------------
const EditModal = ({ form, setForm, onSubmit, onClose }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
    >
      <h2 className="text-xl font-bold mb-4">Edit School</h2>

      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        <input
          className="border p-2 rounded"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="School Name"
        />

        <input
          className="border p-2 rounded"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email"
        />

        <textarea
          rows={3}
          className="border p-2 rounded"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          placeholder="Address"
        />

        <div className="flex justify-end gap-3 mt-2">
          <button
            onClick={onClose}
            type="button"
            className="bg-gray-300 px-4 py-2 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-700 text-white px-4 py-2 rounded"
          >
            Save
          </button>
        </div>
      </form>
    </motion.div>
  </div>
);

// -----------------------------------------------------
// DELETE MODAL
// -----------------------------------------------------
const DeleteModal = ({ onConfirm, onClose }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-white p-6 rounded-xl w-full max-w-sm shadow-xl"
    >
      <h2 className="text-xl font-bold text-red-600 mb-4">Delete School?</h2>
      <p className="text-gray-600 mb-6">This action cannot be undone.</p>

      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 bg-red-600 text-white rounded"
        >
          Delete
        </button>
      </div>
    </motion.div>
  </div>
);

export default SchoolManagement;
